import React, { useCallback, useEffect, useMemo, useState } from "react";
import { tableUseStyle } from "../../etc/tableusestyle";
import { DataGridPremium, GridColDef, GridToolbar } from "@mui/x-data-grid-premium";
import { Select, Option } from "@mui/joy";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFetch, postFetch } from "../../../../../fetch/fetch";
import CustomInput from "../../etc/custominput";

/**
 * 윈터 스쿨 등록 관리 > '대기자 관리' — 통합 페이지(suneungsunbae.com/winterregister)에서 들어온 대기 신청만 (waitingList source='winterregister').
 * 기존 광고 랜딩·/registerWaiting2026 대기(winter26)는 기존 대기자 리스트 화면 그대로.
 * 서버: GET /dashboard/payments/winterpay/waitinglist · POST /waiting/status { id, status } · POST /waiting/memo { id, memo }
 * 처리 상태: waiting(대기) / contacted(연락함) / registered(등록) / canceled(취소 — 고객이 알림톡 '대기 취소하기'로 취소해도 canceled)
 */

const WINTER_BRANCHES = ["대치", "강남", "송파", "분당", "수성", "김포", "신촌", "강동", "하남", "센텀", "동탄"];
const STATUS_OPTIONS: { value: string; label: string; color: string }[] = [
    { value: "waiting", label: "대기", color: "#1B64DA" },
    { value: "contacted", label: "연락함", color: "#A95B00" },
    { value: "registered", label: "등록", color: "#087A4B" },
    { value: "canceled", label: "취소", color: "#8B95A1" },
];
const toastOptions: any = { position: "bottom-right", autoClose: 3000, hideProgressBar: true, theme: "colored" };

const formatDate = (v: any) => {
    if (!v) return "";
    const d = new Date(v);
    if (isNaN(d.getTime())) return String(v);
    const p = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};
const formatDay = (v: any) => {
    if (!v) return "";
    const s = typeof v === "string" ? v.slice(0, 10) : formatDate(v).slice(0, 10);
    return s;
};
const formatPhone = (v: any) => {
    const d = String(v || "").replace(/[^0-9]/g, "");
    if (d.length === 11) return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
    return String(v || "");
};

const WinterWaitingList: React.FC<any> = (props) => {

    const classes = tableUseStyle();
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [branch, setBranch] = useState<string>("전체");
    const [program, setProgram] = useState<string>("전체");
    const [status, setStatus] = useState<string>("전체");
    const [searchText, setSearchText] = useState<string>("");

    const fetchList = useCallback(async () => {
        try {
            setLoading(true);
            const result: any = await getFetch("/dashboard/payments/winterpay/waitinglist");
            if (result.message === "success" && Array.isArray(result.data)) {
                setRows(result.data.map((r: any) => ({ ...r, locationShort: String(r.locationKo || "").replace(/점$/, "") })));
            } else {
                toast.error("목록을 불러오지 못했습니다", toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("목록을 불러오지 못했습니다", toastOptions);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchList(); }, [fetchList]);

    const visibleRows = useMemo(() => {
        const text = searchText.trim();
        const isNumber = text.length > 0 && !isNaN(+text[0]);
        return rows.filter((row: any) => {
            if (branch !== "전체" && row.locationShort !== branch) return false;
            if (program !== "전체" && row.program !== program) return false;
            if (status !== "전체" && row.status !== status) return false;
            if (!text) return true;
            if (isNumber) return String(row.phoneNumber || "").replace(/[^0-9]/g, "").includes(text.replace(/[^0-9]/g, ""));
            return String(row.name || "").includes(text);
        });
    }, [rows, branch, program, status, searchText]);

    // 집계: 대기(waiting) 기준 지점×프로그램
    const counts = useMemo(() => {
        const c: Record<string, { pre: number; regular: number }> = {};
        WINTER_BRANCHES.forEach(b => { c[b] = { pre: 0, regular: 0 }; });
        rows.forEach((r: any) => {
            if (r.status !== "waiting") return;
            if (!c[r.locationShort]) c[r.locationShort] = { pre: 0, regular: 0 };
            if (r.program === "pre") c[r.locationShort].pre += 1; else if (r.program === "regular") c[r.locationShort].regular += 1;
        });
        return c;
    }, [rows]);
    const waitingTotal = rows.filter((r: any) => r.status === "waiting").length;

    const changeStatus = async (row: any, next: string) => {
        if (!next || next === row.status) return;
        if (next === "canceled" && !window.confirm(`${row.name} 대기 신청을 취소 처리할까요?`)) return;
        const prev = row.status;
        setRows(p => p.map(r => r.id === row.id ? { ...r, status: next } : r));
        try {
            const r: any = await postFetch("/dashboard/payments/winterpay/waiting/status", { id: row.id, status: next });
            if (r.message !== "success") throw new Error(r.message);
        } catch (e) {
            console.log(e);
            setRows(p => p.map(r => r.id === row.id ? { ...r, status: prev } : r));
            toast.error("상태 저장에 실패했습니다", toastOptions);
        }
    };

    const handleRowUpdate = async (newRow: any, oldRow: any) => {
        try {
            if ((newRow.adminEtc || "") !== (oldRow.adminEtc || "")) {
                const r: any = await postFetch("/dashboard/payments/winterpay/waiting/memo", { id: newRow.id, memo: newRow.adminEtc || "" });
                if (r.message !== "success") throw new Error(r.message);
            }
            return newRow;
        } catch (e) {
            console.log(e);
            toast.error("메모 저장에 실패했습니다", toastOptions);
            return oldRow;
        }
    };

    const columns: GridColDef[] = [
        { field: "createdAt", headerName: "신청일시", width: 140, valueFormatter: (params: any) => formatDate(params.value) },
        { field: "locationKo", headerName: "지점", width: 80 },
        { field: "programKo", headerName: "프로그램", width: 90 },
        { field: "name", headerName: "이름", width: 100 },
        { field: "phoneNumber", headerName: "전화번호", width: 130, valueFormatter: (params: any) => formatPhone(params.value) },
        { field: "gender", headerName: "성별", width: 70, valueFormatter: (params: any) => params.value === "female" ? "여" : params.value === "male" ? "남" : "" },
        { field: "grade", headerName: "학년", width: 80, valueFormatter: (params: any) => params.value === "current" ? "재학생" : params.value === "n" ? "N수생" : "" },
        { field: "enrollmentDate", headerName: "입실 희망일", width: 110, valueFormatter: (params: any) => formatDay(params.value) },
        { field: "etc", headerName: "요청사항", width: 200 },
        {
            field: "status", headerName: "처리 상태", width: 130,
            renderCell: (params: any) => {
                const opt = STATUS_OPTIONS.find(o => o.value === params.value) || STATUS_OPTIONS[0];
                return (
                    <Select size="sm" value={params.value || "waiting"} onChange={(e, v) => changeStatus(params.row, v as string)} sx={{ minWidth: "110px", color: opt.color, fontWeight: 600 }}>
                        {STATUS_OPTIONS.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                    </Select>
                );
            },
        },
        { field: "adminEtc", headerName: "메모", width: 220, flex: 1, editable: true },
    ];

    const cellStyle: React.CSSProperties = { padding: "8px 14px", borderBottom: "1px solid #e5e8eb", fontFamily: "Apple_R", fontSize: "14px", color: "#191f28" };

    return (
        <div style={{ width: "96%" }}>
            <div style={{ fontFamily: "Apple_B", marginTop: "50px", fontSize: "18px" }}>대기자 관리</div>
            <div style={{ marginTop: "8px", fontSize: "13px", color: "#6B7684" }}>
                통합 페이지(suneungsunbae.com/winterregister)에서 모집 마감 지점에 대기 신청한 분들입니다. 광고 랜딩·기존 대기 신청 페이지의 윈터 대기는 기존 대기자 리스트에 그대로 있습니다. 고객이 알림톡 '대기 취소하기'로 취소하면 '취소'로 바뀝니다.
            </div>

            <div style={{ display: "flex", alignItems: "center", marginTop: "20px", flexWrap: "wrap", gap: "12px" }}>
                <ToggleButtonGroup color="primary" size="small" exclusive value={branch} onChange={(e, v) => { if (v) setBranch(v); }}>
                    <ToggleButton value="전체">전체</ToggleButton>
                    {WINTER_BRANCHES.map(b => <ToggleButton value={b} key={b}>{b}</ToggleButton>)}
                </ToggleButtonGroup>
                <ToggleButtonGroup color="primary" size="small" exclusive value={program} onChange={(e, v) => { if (v) setProgram(v); }}>
                    <ToggleButton value="전체">전체</ToggleButton>
                    <ToggleButton value="pre">프리윈터</ToggleButton>
                    <ToggleButton value="regular">정규윈터</ToggleButton>
                </ToggleButtonGroup>
                <ToggleButtonGroup color="primary" size="small" exclusive value={status} onChange={(e, v) => { if (v) setStatus(v); }}>
                    <ToggleButton value="전체">전체</ToggleButton>
                    {STATUS_OPTIONS.map(o => <ToggleButton value={o.value} key={o.value}>{o.label}</ToggleButton>)}
                </ToggleButtonGroup>
                <div style={{ marginLeft: "auto" }}>
                    <CustomInput value={searchText} onChange={(e: any) => setSearchText(e.target.value)} placeholder="이름 or 전화번호" />
                </div>
            </div>

            <div className={classes.root} style={{ height: "560px", width: "100%", marginTop: "16px", backgroundColor: "white" }}>
                <DataGridPremium
                    rows={visibleRows}
                    columns={columns}
                    density="compact"
                    loading={loading}
                    disableRowSelectionOnClick
                    processRowUpdate={handleRowUpdate}
                    onProcessRowUpdateError={(e) => console.log(e)}
                    slots={{ toolbar: GridToolbar }}
                />
            </div>

            <div style={{ marginTop: "28px", marginBottom: "60px", display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                <div style={{ fontFamily: "Apple_B", fontSize: "16px", marginBottom: "10px", width: "360px" }}>대기 중 집계 (처리 상태 '대기' 기준)</div>
                <table style={{ borderCollapse: "collapse", width: "360px", border: "1px solid #e5e8eb" }}>
                    <tbody>
                        <tr style={{ backgroundColor: "#f5f6f8" }}>
                            <td style={{ ...cellStyle, fontFamily: "Apple_B" }}>총 대기</td>
                            <td style={{ ...cellStyle, fontFamily: "Apple_B", textAlign: "right" }} colSpan={2}>{waitingTotal}명</td>
                        </tr>
                        <tr style={{ backgroundColor: "#fafbfc" }}>
                            <td style={cellStyle}>지점</td>
                            <td style={{ ...cellStyle, textAlign: "right" }}>프리윈터</td>
                            <td style={{ ...cellStyle, textAlign: "right" }}>정규윈터</td>
                        </tr>
                        {Object.keys(counts).map((b, i, arr) => {
                            const last = i === arr.length - 1;
                            const st = last ? { ...cellStyle, borderBottom: "none" } : cellStyle;
                            return (
                                <tr key={b}>
                                    <td style={st}>{b}</td>
                                    <td style={{ ...st, textAlign: "right" }}>{counts[b].pre}명</td>
                                    <td style={{ ...st, textAlign: "right" }}>{counts[b].regular}명</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default WinterWaitingList;
